import React, { useState, useEffect, useRef } from "react"
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap"
import { useMutation, useLazyQuery } from "@apollo/client"
import { toast } from "react-toastify"
import { TRIGGER_WOOCOMMERCE_SYNC, GET_SYNC_STATUS, RESET_SYNC_JOB } from "../../graphql/mutations/sync"

const SYNC_OPTIONS = [
  { value: "all",      label: "All (products & orders)" },
  { value: "products", label: "Products only" },
  { value: "orders",   label: "Orders only" },
]

// Minutes before a running job is considered potentially stuck
const WARN_AFTER_MINUTES = { products: 15, orders: 5, all: 15 }

const elapsedSince = (isoString) => {
  if (!isoString) return null
  const ms = Date.now() - new Date(isoString).getTime()
  const totalSeconds = Math.floor(ms / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`
}

const formatTime = (isoString) => {
  if (!isoString) return "Never"
  return new Date(isoString).toLocaleString("nl-NL", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  })
}

const minutesSince = (isoString) => {
  if (!isoString) return 0
  return (Date.now() - new Date(isoString).getTime()) / 60000
}

const WooCommerceSyncModal = ({ isOpen, onClose }) => {
  const [syncType, setSyncType] = useState("all")
  const [triggered, setTriggered] = useState(false)
  const prevRunningRef = useRef(false)
  const pollIntervalRef = useRef(null)

  const [triggerSync, { loading: triggering }] = useMutation(TRIGGER_WOOCOMMERCE_SYNC)
  const [resetJob, { loading: resetting }] = useMutation(RESET_SYNC_JOB)

  const [fetchStatus, { data: statusData }] = useLazyQuery(GET_SYNC_STATUS, {
    fetchPolicy: "network-only",
    onError: () => {},
  })

  const jobs = statusData?.GetSyncStatus || []
  const anyRunning = jobs.some(j => j.running)

  const startPolling = (type) => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current)
    pollIntervalRef.current = setInterval(() => {
      fetchStatus({ variables: { type } })
    }, 3000)
  }

  const stopPolling = () => {
    clearInterval(pollIntervalRef.current)
    pollIntervalRef.current = null
  }

  useEffect(() => {
    if (isOpen) {
      // Fetch status immediately on open to show any currently running/stuck jobs
      fetchStatus({ variables: { type: syncType } })
      startPolling(syncType)
    } else {
      stopPolling()
      setTriggered(false)
      prevRunningRef.current = false
    }
    return () => stopPolling()
  }, [isOpen])

  // Re-fetch when sync type changes
  useEffect(() => {
    if (isOpen) {
      fetchStatus({ variables: { type: syncType } })
      startPolling(syncType)
    }
  }, [syncType])

  // Detect running → done
  useEffect(() => {
    if (!triggered) return
    if (prevRunningRef.current && !anyRunning) {
      toast.success("WooCommerce sync completed!")
      stopPolling()
      setTriggered(false)
    }
    prevRunningRef.current = anyRunning
  }, [anyRunning, triggered])

  const handleConfirm = async () => {
    try {
      await triggerSync({ variables: { type: syncType } })
      setTriggered(true)
      prevRunningRef.current = false
      toast.info("WooCommerce sync started...")
      fetchStatus({ variables: { type: syncType } })
      startPolling(syncType)
    } catch (err) {
      toast.error(err.message)
      // Show status so the Force reset button appears for stuck jobs
      fetchStatus({ variables: { type: syncType } })
      startPolling(syncType)
    }
  }

  const handleReset = async (jobType) => {
    try {
      await resetJob({ variables: { type: jobType } })
      toast.warning(`Sync job "${jobType}" has been reset.`)
      fetchStatus({ variables: { type: syncType } })
    } catch (err) {
      toast.error(err.message)
    }
  }

  const handleClose = () => {
    setSyncType("all")
    setTriggered(false)
    stopPolling()
    onClose()
  }

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered>
      <ModalHeader toggle={handleClose}>WooCommerce Synchronisation</ModalHeader>
      <ModalBody>
        <p>
          Are you sure you want to start a new synchronisation? This can impact
          the performance of the website and portal and it&apos;s recommended to
          do this only outside of NL business hours.
        </p>

        <label htmlFor="wc_syncType_select" className="form-label">
          <strong>What to synchronise:</strong>
        </label>
        <select
          id="wc_syncType_select"
          className="form-select mb-3"
          value={syncType}
          onChange={e => setSyncType(e.target.value)}
          disabled={triggering || anyRunning}
        >
          {SYNC_OPTIONS.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        {jobs.length > 0 && (
          <div className="border rounded p-2" style={{ fontSize: "0.85rem" }}>
            {jobs.map(job => {
              const elapsed = job.running ? elapsedSince(job.lastStart) : null
              const isStuck = job.running && minutesSince(job.lastStart) > WARN_AFTER_MINUTES[job.type]
              return (
                <div key={job.type} className="mb-2">
                  <div className="d-flex align-items-center justify-content-between">
                    <span className="text-capitalize fw-semibold">{job.type}</span>
                    {job.running ? (
                      <span className={isStuck ? "text-danger" : "text-warning"}>
                        <i className={`bx bx-loader-alt ${isStuck ? "" : "bx-spin"} me-1`} />
                        Running... {elapsed && `(${elapsed})`}
                      </span>
                    ) : (
                      <span className="text-muted">
                        Last sync: {formatTime(job.lastEnd)}
                      </span>
                    )}
                  </div>
                  {isStuck && (
                    <div className="d-flex align-items-center justify-content-between mt-1">
                      <small className="text-danger">
                        Running longer than expected — may be stuck.
                      </small>
                      <Button
                        size="sm"
                        color="danger"
                        outline
                        disabled={resetting}
                        onClick={() => handleReset(job.type)}
                      >
                        Force reset
                      </Button>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleClose}>
          Close
        </Button>
        <Button
          color="primary"
          disabled={triggering || anyRunning}
          onClick={handleConfirm}
        >
          {triggering ? "Starting..." : anyRunning ? "Sync running..." : "OK"}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default WooCommerceSyncModal
